/**
 * Executive Command Center — CEO cockpit for Kevin Pay Pay Mumbere.
 *
 * Cross-department situational awareness: today's operations pulse,
 * finance snapshot, HR headcount, IT/system health, safety incidents,
 * and the executive inbox. Uses live counts from `/api/dept-tasks/counts`
 * and every department's public list endpoint.
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Crown, TrendingUp, DollarSign, Users, Cpu, Shield, Cog, ArrowRightLeft,
  BarChart3, Sparkles, Building2, Truck, Award,
} from 'lucide-react';
import { KpiTile, DeptInbox, RouteTaskDialog, PriorityBanner, DEPT_LIST } from './DeptTaskShared';
import MyInboxAggregate from './MyInboxAggregate';
import { useNavigate } from 'react-router-dom';
import { isAdminRow } from '../shared/MetroKindHeader';
import CorporateEvaluationPanel from './CorporateEvaluationPanel';
import { SalesLeaderboardPanel, RiskAssessmentPanel, GrowthProjectionPanel, FinanceScorecardPanel, SafetyRiskPanel, HrLeadershipPanel, GeoFilterProvider } from './CeoStrategyPanels';
import { ExecutiveLeaderboardSummary } from './LeaderboardCard';
import ExecOperationsTiles from './ExecOperationsTiles';
import ResidentComplianceHealthTile from './ResidentComplianceHealthTile';
import NobleAIBranchAnalyticsTile from './NobleAIBranchAnalyticsTile';
import CEOFleetSummary from './CEOFleetSummary';
import UnifiedGeoRoleFilter, { useBoardFilter } from '../UnifiedGeoRoleFilter';
import { DepartmentDetails } from './DepartmentDetails';
import { DepartmentSectionMenu } from './DepartmentNavigation';
import { DEPT_BY_KEY } from './AdminDepartments';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ExecutiveCommandCenter({ user, onJumpDept, boardFilter: boardFilterProp }) {
  const navigate = useNavigate();
  // Iter-111 · Fix 2 — the parent (CompanyManagerDashboard) lifts the
  // Enterprise Pivot Filter instance and passes it down; the internal
  // instance remains a fallback for standalone mounts.
  const internalFilter = useBoardFilter('ceo-board');
  const boardFilter = boardFilterProp || internalFilter;
  const [counts, setCounts] = useState({});
  const [snapshot, setSnapshot] = useState({
    properties: 0, staff: 0, brokers: 0, contracts: 0,
  });
  const [tasks, setTasks] = useState([]);
  const [route, setRoute] = useState(false);
  const [adminCount, setAdminCount] = useState(0);
  const [staffCountAll, setStaffCountAll] = useState(0);
  void adminCount; void staffCountAll;
  const [kpiSnap, setKpiSnap] = useState({});

  // Iter-105 · Item 5 — CEO KPI snapshot with previous-window comparison
  // for the delta chips. Re-fetches whenever the board filter changes.
  useEffect(() => {
    if (!boardFilter?.windowDays) return;
    // Section 2 (iter-138) — use the full pivot queryString so state /
    // city / country / region / view_as / date_from-to all reach the
    // KPI snapshot endpoint.
    const qs = boardFilter?.queryString ? `&${boardFilter.queryString}` : '';
    fetch(`${API}/api/executive/kpi-snapshot?window_days=${boardFilter.windowDays}${qs}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && d.kpis && setKpiSnap(d.kpis))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardFilter?.windowDays, boardFilter?.queryString, boardFilter?.refreshTick]);

  useEffect(() => {
    (async () => {
      try {
        const qs = boardFilter?.queryString ? `?${boardFilter.queryString}` : '';
        const [c, props, emps, contracts, brokers, dtasks] = await Promise.all([
          fetch(`${API}/api/dept-tasks/counts`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : {})),
          fetch(`${API}/api/properties${qs}`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : [])),
          fetch(`${API}/api/company/employees${qs}`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : [])),
          fetch(`${API}/api/contracts${qs}`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : [])),
          fetch(`${API}/api/brokers${qs}`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : [])),
          fetch(`${API}/api/dept-tasks?target_department=executive`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : [])),
        ]);
        setCounts(c || {});
        setSnapshot({
          properties: props.length || 0,
          staff: emps.filter?.((u) => ['supervisor', 'crew_leader', 'agent'].includes(u.role)).length || 0,
          brokers: brokers.length || 0,
          contracts: contracts.length || 0,
        });
        const empList = Array.isArray(emps) ? emps : [];
        const admins = empList.filter(isAdminRow).length;
        setAdminCount(admins);
        setStaffCountAll(empList.length - admins);
        setTasks(Array.isArray(dtasks) ? dtasks : []);
      } catch (_) { /* noop */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardFilter?.queryString, boardFilter?.refreshTick]);

  const totalOpen = Object.values(counts).reduce((acc, v) => acc + ((v?.open || 0) + (v?.in_progress || 0)), 0);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid="executive-command-center">
      {/* Personal CEO header */}
      <div
        className="rounded-2xl p-4 sm:p-6 border-2 flex flex-wrap items-start gap-4"
        style={{
          background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 60%, #2D2D2D 100%)',
          borderColor: 'rgba(255,214,10,0.5)',
        }}
        data-testid="exec-hero"
      >
        <div className="h-16 w-16 rounded-xl flex items-center justify-center flex-shrink-0"
             style={{ backgroundColor: 'rgba(255,214,10,0.15)', border: '2px solid #FFD60A' }}>
          <Crown className="h-8 w-8 text-[#FFD60A]" />
        </div>
        <div className="flex-1 min-w-0 basis-48">
          <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#FFD60A]">
            Executive Office · Command Center
          </p>
          <h1 className={`${user?.name ? 'necs-dashboard-name text-lg sm:text-xl' : 'text-2xl sm:text-3xl font-bold'} text-white mt-1`}
              data-testid="executive-dashboard-name">
            {user?.name || 'CHIEF EXECUTIVE OFFICER'}
          </h1>
          <p className="text-sm text-zinc-300 mt-1">
            Chief Executive Officer · Founder · Noble Empire Cleaning Services, LLC
          </p>
          <p className="text-xs text-zinc-400 mt-3 max-w-3xl leading-relaxed">
            Set strategic direction, review live operations, and route work to the responsible
            department. Every KPI below is live — no waiting for morning briefings.
          </p>
        </div>
        <Button
          onClick={() => setRoute(true)}
          className="bg-[#FFD60A] hover:bg-[#EAB308] text-black font-bold"
          data-testid="exec-route-work-btn"
        >
          <ArrowRightLeft className="h-4 w-4 mr-2" /> Delegate Work
        </Button>
      </div>

      {/* Iter-112 · Item 1 — §2 Enterprise Pivot Filter directly below the
          presentation, above the tabs area. The standalone corporate
          slicer (MetroKindHeader) was retired — Corporate Admin lives in
          the filter's Role row. */}
      <DepartmentDetails dept={DEPT_BY_KEY.executive} prefix="executive" accent="#FFD60A" />
      <UnifiedGeoRoleFilter filter={boardFilter} testIdPrefix="unified-filter-ceo-board" />
      <DepartmentSectionMenu prefix="executive" sections={[{key:'exec-hero',label:'Executive Overview'},{key:'unified-filter-ceo-board-live-metrics',label:'Live Metrics'},{key:'exec-department-directory',label:'Department Command Centers'}]}/>

      <PriorityBanner tasks={tasks} />

      {/* Company snapshot — Iter-105 · Item 5 delta chips wired via
          `kpiSnap.previous`. */}
      <section>
        <h2 className="text-xs uppercase tracking-widest text-[#FFD60A] mb-3 font-semibold">Company Snapshot</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiTile icon={<Building2 className="h-5 w-5" />} label="Properties" value={snapshot.properties}
                   previousValue={kpiSnap.properties?.previous}
                   positiveIsGood={kpiSnap.properties?.positive_is_good ?? true}
                   sub="Signed contracts" tone="#0ea5e9" testid="exec-kpi-properties" />
          <KpiTile icon={<Users className="h-5 w-5" />} label="Field Staff" value={snapshot.staff}
                   previousValue={kpiSnap.field_staff?.previous}
                   positiveIsGood={kpiSnap.field_staff?.positive_is_good ?? true}
                   sub="Supervisors + Crew + Agents" tone="#22c55e" testid="exec-kpi-staff" />
          <KpiTile icon={<Truck className="h-5 w-5" />} label="Broker Suppliers" value={snapshot.brokers}
                   previousValue={kpiSnap.brokers?.previous}
                   positiveIsGood={kpiSnap.brokers?.positive_is_good ?? true}
                   sub="On the ledger" tone="#a855f7" testid="exec-kpi-brokers" />
          <KpiTile icon={<Award className="h-5 w-5" />} label="Contracts on file" value={snapshot.contracts}
                   previousValue={kpiSnap.contracts?.previous}
                   positiveIsGood={kpiSnap.contracts?.positive_is_good ?? true}
                   sub="Executed & stored" tone="#f59e0b" testid="exec-kpi-contracts" />
        </div>
      </section>

      {/* Iter-105 · Item 5 — Operational KPI row with delta chips for
          active routes, on-track %, delayed routes, compliance %,
          service line adoption, and reward badges. */}
      <section>
        <h2 className="text-xs uppercase tracking-widest text-[#FFD60A] mb-3 font-semibold">Operational Signals</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiTile icon={<Truck className="h-5 w-5" />} label="Active Routes"
                   value={kpiSnap.active_routes?.current ?? 0}
                   previousValue={kpiSnap.active_routes?.previous}
                   positiveIsGood={kpiSnap.active_routes?.positive_is_good ?? true}
                   tone="#22c55e" testid="exec-kpi-active-routes" />
          <KpiTile icon={<TrendingUp className="h-5 w-5" />} label="On-Track %"
                   value={`${kpiSnap.on_track_pct?.current ?? 0}%`}
                   previousValue={kpiSnap.on_track_pct?.previous}
                   positiveIsGood={kpiSnap.on_track_pct?.positive_is_good ?? true}
                   tone="#0ea5e9" testid="exec-kpi-ontrack" />
          <KpiTile icon={<Shield className="h-5 w-5" />} label="Delayed Routes"
                   value={kpiSnap.delayed_routes?.current ?? 0}
                   previousValue={kpiSnap.delayed_routes?.previous}
                   positiveIsGood={kpiSnap.delayed_routes?.positive_is_good ?? false}
                   tone="#ef4444" testid="exec-kpi-delayed" />
          <KpiTile icon={<BarChart3 className="h-5 w-5" />} label="Compliance %"
                   value={`${kpiSnap.compliance_pct?.current ?? 0}%`}
                   previousValue={kpiSnap.compliance_pct?.previous}
                   positiveIsGood={kpiSnap.compliance_pct?.positive_is_good ?? true}
                   tone="#a855f7" testid="exec-kpi-compliance" />
          <KpiTile icon={<Sparkles className="h-5 w-5" />} label="Service Lines"
                   value={kpiSnap.service_line_adoption?.current ?? 0}
                   previousValue={kpiSnap.service_line_adoption?.previous}
                   positiveIsGood={kpiSnap.service_line_adoption?.positive_is_good ?? true}
                   sub="Active adoption" tone="#22d3ee" testid="exec-kpi-service-lines" />
          <KpiTile icon={<Award className="h-5 w-5" />} label="Reward Badges"
                   value={kpiSnap.reward_badge_count?.current ?? 0}
                   previousValue={kpiSnap.reward_badge_count?.previous}
                   positiveIsGood={kpiSnap.reward_badge_count?.positive_is_good ?? true}
                   sub="Silver + Gold + Platinum" tone="#FFD60A" testid="exec-kpi-reward-badges" />
        </div>
      </section>

      {/* Iter-90 · Operations Intelligence summary tiles.
          Read-only rollup — full map + accuracy trend live inside the
          Operations dept "Live Ops" tab. */}
      <ExecOperationsTiles />

      {/* Iter-99 · Resident Reward Distribution — read-only company-wide
          Silver / Gold / Platinum split. Compute happens at the Operations
          layer; the CEO Board simply displays the health stat. */}
      <ResidentComplianceHealthTile />

      {/* Iter-102 · Item 6 — Noble AI Analytics tile. Read-only display
          of weekly branch → CS escalation counts. CS Department owns
          the compute; this is the summary surface for the CEO. */}
      <NobleAIBranchAnalyticsTile />

      {/* Procurement Fleet · big-picture only. Detail lives inside each
          department's Director view. */}
      <CEOFleetSummary />

      {/* Iter-82 → iter-85 · CEO STRATEGY DASHBOARD.
          Iter-105 · Item 1 — `GeoFilterProvider` now bridges the six
          panels to this Board's own `boardFilter`, so scope changes
          are scoped to the CEO Board only. */}
      <GeoFilterProvider boardFilter={boardFilter}>
        <SalesLeaderboardPanel />
        <FinanceScorecardPanel />
        <SafetyRiskPanel />
        <HrLeadershipPanel />
        <RiskAssessmentPanel />
        <GrowthProjectionPanel />
      </GeoFilterProvider>

      {/* Cross-department workload */}
      <section data-testid="exec-department-directory">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-xs uppercase tracking-widest text-[#FFD60A] font-semibold">Cross-Department Workload</h2>
          <span className="text-[11px] text-zinc-500">{totalOpen} open items across NECS</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {DEPT_LIST.filter((d) => d.key !== 'executive').map((d) => {
            const c = counts[d.key] || { open: 0, in_progress: 0, resolved: 0, total: 0 };
            const openish = c.open + c.in_progress;
            const tone = openish >= 5 ? '#ef4444' : openish >= 1 ? '#FFD60A' : '#22c55e';
            const Icon = ICON_MAP[d.key] || Cog;
            return (
              <button
                key={d.key}
                type="button"
                onClick={() => onJumpDept?.(d.key)}
                className="text-left bg-zinc-900 border-2 rounded-lg p-4 transition hover:-translate-y-0.5"
                style={{ borderColor: `${tone}40` }}
                data-testid={`exec-dept-tile-${d.key}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="p-2 rounded-lg" style={{ backgroundColor: `${tone}22`, color: tone }}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <p className="text-sm font-bold text-white uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {d.label}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {c.open > 0 && <Badge className="bg-sky-600 text-white text-[10px]">Open · {c.open}</Badge>}
                  {c.in_progress > 0 && <Badge className="bg-blue-600 text-white text-[10px]">In progress · {c.in_progress}</Badge>}
                  {c.resolved > 0 && <Badge className="bg-emerald-600 text-white text-[10px]">Resolved · {c.resolved}</Badge>}
                  {c.total === 0 && <span className="text-[11px] text-zinc-500">No tasks routed</span>}
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* CEO Corporate Evaluation — big-picture scorecard for the whole
          NECS platform. Sits above the Executive Inbox so the strategic
          view precedes the tactical queue. */}
      <CorporateEvaluationPanel />

      {/* Executive inbox */}
      <DeptInbox deptKey="executive" title="EXECUTIVE INBOX" />

      {/* Aggregate view — unified queue across every department the CEO
          has clearance for. CEO has global access so this shows all 12
          department inboxes filtered by priority + status chips. */}
      <MyInboxAggregate accessibleDepts={DEPT_LIST.map((d) => d.key)} />

      {/* Iter-112 · Item 1 — §4 high-efficiency leading boards at the
          very bottom of the CEO Dashboard. */}
      <ExecutiveLeaderboardSummary />

      <RouteTaskDialog open={route} onClose={() => setRoute(false)} onSaved={() => window.location.reload()} defaultDept="operations" />
    </div>
  );
}

const ICON_MAP = {
  operations: Cog,
  finance: DollarSign,
  hr: Users,
  it: Cpu,
  legal: Shield,
  sales: TrendingUp,
  marketing: Sparkles,
  'customer-service': BarChart3,
  procurement: Truck,
  safety: Shield,
  training: Award,
};
